/**
 * Peyote patterns — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stitch_charts`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const peyotePatternsPack: ToilePack = {
    slug: 'peyote-patterns',
    category: 'craft',
    name: 'Peyote patterns',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1216, h: 832},
    readings: [
        {key: 'geometric', label: 'Geometric', subject: 'a peyote bracelet chart in offset bead columns, an interlocking zigzag pattern in black, gold and white repeating along the length of the band'},
        {key: 'floral', label: 'Floral', subject: 'a peyote bracelet chart with a row of small roses and green leaves on a cream ground, each bead a small rounded rectangle offset half a bead from its neighbours'},
        {key: 'galaxy', label: 'Galaxy', subject: 'a peyote cuff chart of a starry night sky shading from deep navy to purple, with scattered white beads as stars and a crescent moon at the centre'},
    ],
    styles: [
        {key: 'colour', label: 'Colour chart', style: 'a clean colour chart, every bead filled flat in its colour with fine light-gray grid lines between them', palette: 'a limited palette of the seed bead colours named', framing: 'the chart centred on a plain white sheet with an even margin'},
        {key: 'symbol', label: 'Symbol chart', style: 'a chart for black-and-white printing, every bead carrying one small black symbol over a pale colour so each shade stays distinct', palette: 'pale colours kept under crisp black symbols', framing: 'the chart filling most of a white sheet with a narrow clean margin'},
        {key: 'graph', label: 'Graph paper', style: 'a hand-coloured worksheet, each bead filled in with felt-tip pens, slightly uneven strokes and a few pencil guide marks, on blue squared graph paper', palette: 'bright felt-tip colours on pale blue grid paper', framing: 'the chart drawn on a plain sheet with a small margin all round'},
    ],
    directions: ['tribal', 'hearts', 'skulls'],
    directionHint: 'try: tribal',
    seo: {
        title: 'Peyote patterns: geometric, floral and galaxy bracelets',
        metaDescription: 'Nine peyote bracelet charts: geometric, floral and galaxy bands, bead by bead, in colour, symbol and hand-coloured styles.',
        h1: 'Peyote patterns: geometric, floral and galaxy bracelets',
        tagline: 'Long bead charts with each column offset by half a bead, for bracelets and cuffs.',
        intro: 'Peyote stitch weaves seed beads into a flat band, mostly for bracelets and cuffs. The chart shows each bead in offset columns, so the band can be followed row by row. These nine cover a geometric zigzag, a floral band and a galaxy cuff. Each comes as a colour chart, a symbol chart, or a hand-coloured sheet. Type a different theme and the same layout carries it.',
        useCases: [
            'a beaded bracelet',
            'a wide cuff',
            'a band for a leather strap',
            "a gift bracelet in the recipient's colours",
        ],
        faq: [
            {q: 'How do the columns line up?', a: 'Each column sits half a bead from its neighbour, so the beads interlock across the band.'},
            {q: 'Can I change the colours?', a: 'Yes. Type the bead colours you want. The samples here keep theirs.'},
            {q: 'Which chart is clearest?', a: 'The colour chart, with each bead a flat fill.'},
        ],
    },
}
